'use client';

import React, { useState } from 'react';
import Link from 'next/link';
import Header from '@/components/Header';
import Footer from '@/components/Footer';
import { useLanguage } from '@/context/LanguageContext';
import { getTranslations } from '@/lib/i18n';

interface VolunteerForm {
  name: string; email: string; phone: string; city: string; skills: string; availability: string; interest: string; message: string;
}

export default function GetInvolvedPage() {
  const { language } = useLanguage();
  const t = getTranslations(language);
  const kn = language === 'kn';

  const [form, setForm] = useState<VolunteerForm>({ name: '', email: '', phone: '', city: '', skills: '', availability: '', interest: '', message: '' });
  const [submitted, setSubmitted] = useState(false);
  const [errors, setErrors] = useState<Partial<VolunteerForm>>({});

  const validate = () => {
    const e: Partial<VolunteerForm> = {};
    if (!form.name.trim()) e.name = kn ? 'ಹೆಸರು ಅಗತ್ಯ' : 'Name is required';
    if (!form.email.trim() || !/\S+@\S+\.\S+/.test(form.email)) e.email = kn ? 'ಮಾನ್ಯ ಇಮೇಲ್ ಅಗತ್ಯ' : 'Valid email required';
    if (!form.phone.trim()) e.phone = kn ? 'ಫೋನ್ ಅಗತ್ಯ' : 'Phone is required';
    if (!form.interest) e.interest = kn ? 'ಆಸಕ್ತಿಯ ಕ್ಷೇತ್ರ ಆಯ್ಕೆ ಮಾಡಿ' : 'Please select an area of interest';
    return e;
  };

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    const errs = validate();
    if (Object.keys(errs).length > 0) { setErrors(errs); return; }
    setSubmitted(true);
  };

  return (
    <div className="bg-white min-h-screen">
      <Header />
      <main className="pt-24">
        <section className="hero-gradient py-20 px-6 text-white">
          <div className="max-w-4xl mx-auto text-center">
            <div className={`inline-flex items-center gap-2 px-4 py-2 rounded-full bg-white/10 border border-white/20 text-sm mb-8 ${kn ? 'font-kannada' : ''}`}>{t.getInvolved.badge}</div>
            <h1 className={`text-4xl sm:text-5xl font-bold mb-4 ${kn ? 'font-kannada' : ''}`}>
              {t.getInvolved.heroTitle} <span className="text-saffron-light">{t.getInvolved.heroHighlight}</span>
            </h1>
            <p className={`text-white/75 text-lg max-w-2xl mx-auto ${kn ? 'font-kannada' : ''}`}>{t.getInvolved.heroSubtitle}</p>
          </div>
        </section>

        <section className="py-20 bg-white">
          <div className="max-w-6xl mx-auto px-6">
            <div className="text-center mb-12">
              <h2 className={`text-3xl font-bold text-indigo mb-2 ${kn ? 'font-kannada' : ''}`}>{t.getInvolved.waysTitle}</h2>
            </div>
            <div className="grid md:grid-cols-3 gap-6">
              {[
                { title: kn ? 'ಸ್ವಯಂಸೇವಕ' : 'Volunteer', icon: '🤝', desc: kn ? 'ನಮ್ಮ ಕಾರ್ಯಕ್ರಮಗಳಿಗೆ ನಿಮ್ಮ ಸಮಯ ಮತ್ತು ಕೌಶಲ್ಯ ಮೀಸಲಿಡಿ. ವಾರಾಂತ್ಯ ಮತ್ತು ವಾರದ ದಿನಗಳ ಸ್ಲಾಟ್‌ಗಳು ಲಭ್ಯ.' : 'Teach, mentor, organize events, or provide professional skills. Weekend and weekday slots available.', color: 'bg-saffron-pale border-saffron/20 text-saffron' },
                { title: kn ? 'ಕಾರ್ಪೊರೇಟ್ ಪಾಲುದಾರಿಕೆ' : 'Corporate Partnership', icon: '🏢', desc: kn ? 'CSR ಪಾಲುದಾರಿಕೆ, ಉದ್ಯೋಗಿ ಸ್ವಯಂಸೇವಕ ಕಾರ್ಯಕ್ರಮಗಳು ಮತ್ತು ಸಂಸ್ಥಾ ಸಹಯೋಗ.' : 'CSR partnerships, employee volunteering programs, and institutional collaborations with full impact reporting.', color: 'bg-indigo-pale border-indigo/20 text-indigo' },
                { title: kn ? 'ಇಂಟರ್ನ್‌ಶಿಪ್' : 'Internships', icon: '🎓', desc: kn ? 'ವಿದ್ಯಾರ್ಥಿಗಳು ಮತ್ತು ತಾಜಾ ಪದವೀಧರರು ಶಿಕ್ಷಣ, ಸಮಾಜ ಕಾರ್ಯ, ಸಂವಹನ ಅಥವಾ ಕಾರ್ಯಾಚರಣೆಯಲ್ಲಿ ಇಂಟರ್ನ್ ಮಾಡಬಹುದು.' : 'Students and fresh graduates can intern with us in education, social work, communications, or operations.', color: 'bg-crimson-pale border-crimson/20 text-crimson' },
              ].map((item) => (
                <div key={item.title} className={`rounded-2xl p-8 border hover-lift card-hover ${item.color.split(' ').slice(0, 2).join(' ')}`}>
                  <div className="text-4xl mb-4">{item.icon}</div>
                  <h3 className={`text-xl font-bold mb-3 ${item.color.split(' ')[2]} ${kn ? 'font-kannada' : ''}`}>{item.title}</h3>
                  <p className={`text-neutral-600 text-sm leading-relaxed ${kn ? 'font-kannada' : ''}`}>{item.desc}</p>
                </div>
              ))}
            </div>
          </div>
        </section>

        <section className="py-16 bg-surface-muted">
          <div className="max-w-3xl mx-auto px-6">
            <div className="text-center mb-10">
              <h2 className={`text-3xl font-bold text-indigo mb-2 ${kn ? 'font-kannada' : ''}`}>{t.getInvolved.volunteerTitle}</h2>
              <p className={`text-neutral-500 text-sm ${kn ? 'font-kannada' : ''}`}>{t.getInvolved.volunteerSubtitle}</p>
            </div>

            {submitted ? (
              <div className="bg-white rounded-3xl p-12 text-center shadow-card">
                <div className="text-5xl mb-4">🎉</div>
                <h3 className={`text-2xl font-bold text-indigo mb-3 ${kn ? 'font-kannada' : ''}`}>{t.getInvolved.successTitle}</h3>
                <p className={`text-neutral-600 mb-6 ${kn ? 'font-kannada' : ''}`}>{t.getInvolved.successMsg}</p>
                <Link href="/home" className={`px-8 py-3 btn-saffron rounded-full font-semibold ${kn ? 'font-kannada' : ''}`}>{kn ? 'ಮುಖಪುಟಕ್ಕೆ ಹಿಂತಿರುಗಿ' : 'Back to Home'}</Link>
              </div>
            ) : (
              <form onSubmit={handleSubmit} className="bg-white rounded-3xl p-8 shadow-card space-y-5">
                <div className="grid sm:grid-cols-2 gap-5">
                  <div>
                    <label className={`block text-sm font-medium text-neutral-700 mb-1.5 ${kn ? 'font-kannada' : ''}`}>{t.getInvolved.name} *</label>
                    <input type="text" value={form.name} onChange={(e) => setForm(f => ({ ...f, name: e.target.value }))} placeholder={kn ? 'ನಿಮ್ಮ ಪೂರ್ಣ ಹೆಸರು' : 'Your full name'}
                      className={`w-full px-4 py-3 border-2 rounded-xl text-sm focus:outline-none transition-colors ${errors.name ? 'border-crimson' : 'border-neutral-200 focus:border-saffron'}`} />
                    {errors.name && <p className={`text-crimson text-xs mt-1 ${kn ? 'font-kannada' : ''}`}>{errors.name}</p>}
                  </div>
                  <div>
                    <label className={`block text-sm font-medium text-neutral-700 mb-1.5 ${kn ? 'font-kannada' : ''}`}>{t.getInvolved.email} *</label>
                    <input type="email" value={form.email} onChange={(e) => setForm(f => ({ ...f, email: e.target.value }))} placeholder="your@email.com"
                      className={`w-full px-4 py-3 border-2 rounded-xl text-sm focus:outline-none transition-colors ${errors.email ? 'border-crimson' : 'border-neutral-200 focus:border-saffron'}`} />
                    {errors.email && <p className={`text-crimson text-xs mt-1 ${kn ? 'font-kannada' : ''}`}>{errors.email}</p>}
                  </div>
                  <div>
                    <label className={`block text-sm font-medium text-neutral-700 mb-1.5 ${kn ? 'font-kannada' : ''}`}>{t.getInvolved.phone} *</label>
                    <input type="tel" value={form.phone} onChange={(e) => setForm(f => ({ ...f, phone: e.target.value }))} placeholder="9876543210"
                      className={`w-full px-4 py-3 border-2 rounded-xl text-sm focus:outline-none transition-colors ${errors.phone ? 'border-crimson' : 'border-neutral-200 focus:border-saffron'}`} />
                    {errors.phone && <p className={`text-crimson text-xs mt-1 ${kn ? 'font-kannada' : ''}`}>{errors.phone}</p>}
                  </div>
                  <div>
                    <label className={`block text-sm font-medium text-neutral-700 mb-1.5 ${kn ? 'font-kannada' : ''}`}>{t.getInvolved.city}</label>
                    <input type="text" value={form.city} onChange={(e) => setForm(f => ({ ...f, city: e.target.value }))} placeholder="Bengaluru"
                      className="w-full px-4 py-3 border-2 border-neutral-200 rounded-xl text-sm focus:outline-none focus:border-saffron transition-colors" />
                  </div>
                  <div>
                    <label className={`block text-sm font-medium text-neutral-700 mb-1.5 ${kn ? 'font-kannada' : ''}`}>{t.getInvolved.interest} *</label>
                    <select value={form.interest} onChange={(e) => setForm(f => ({ ...f, interest: e.target.value }))}
                      className={`w-full px-4 py-3 border-2 rounded-xl text-sm focus:outline-none transition-colors bg-white ${errors.interest ? 'border-crimson' : 'border-neutral-200 focus:border-saffron'}`}>
                      <option value="">{kn ? 'ಕ್ಷೇತ್ರ ಆಯ್ಕೆ ಮಾಡಿ' : 'Select area'}</option>
                      <option value="teaching">{kn ? 'ಬೋಧನೆ / ಟ್ಯೂಟರಿಂಗ್' : 'Teaching / Tutoring'}</option>
                      <option value="health">{kn ? 'ಸಮುದಾಯ ಆರೋಗ್ಯ' : 'Community Health'}</option>
                      <option value="events">{kn ? 'ಕಾರ್ಯಕ್ರಮ ನಿರ್ವಹಣೆ' : 'Event Management'}</option>
                      <option value="communications">{kn ? 'ಸಂವಹನ / ಸಾಮಾಜಿಕ ಮಾಧ್ಯಮ' : 'Communications / Social Media'}</option>
                      <option value="fundraising">{kn ? 'ನಿಧಿ ಸಂಗ್ರಹ' : 'Fundraising'}</option>
                      <option value="admin">{kn ? 'ಆಡಳಿತ' : 'Administration'}</option>
                      <option value="other">{kn ? 'ಇತರ' : 'Other'}</option>
                    </select>
                    {errors.interest && <p className={`text-crimson text-xs mt-1 ${kn ? 'font-kannada' : ''}`}>{errors.interest}</p>}
                  </div>
                  <div>
                    <label className={`block text-sm font-medium text-neutral-700 mb-1.5 ${kn ? 'font-kannada' : ''}`}>{t.getInvolved.availability}</label>
                    <select value={form.availability} onChange={(e) => setForm(f => ({ ...f, availability: e.target.value }))}
                      className="w-full px-4 py-3 border-2 border-neutral-200 rounded-xl text-sm focus:outline-none focus:border-saffron transition-colors bg-white">
                      <option value="">{kn ? 'ಲಭ್ಯತೆ ಆಯ್ಕೆ ಮಾಡಿ' : 'Select availability'}</option>
                      <option value="weekends">{kn ? 'ವಾರಾಂತ್ಯ ಮಾತ್ರ' : 'Weekends only'}</option>
                      <option value="weekdays">{kn ? 'ವಾರದ ದಿನಗಳು' : 'Weekdays'}</option>
                      <option value="evenings">{kn ? 'ಸಂಜೆ' : 'Evenings'}</option>
                      <option value="flexible">{kn ? 'ಹೊಂದಿಕೊಳ್ಳಬಲ್ಲ' : 'Flexible'}</option>
                    </select>
                  </div>
                  <div className="sm:col-span-2">
                    <label className={`block text-sm font-medium text-neutral-700 mb-1.5 ${kn ? 'font-kannada' : ''}`}>{t.getInvolved.skills}</label>
                    <input type="text" value={form.skills} onChange={(e) => setForm(f => ({ ...f, skills: e.target.value }))} placeholder={kn ? 'ಉದಾ: ಬೋಧನೆ, ವಿನ್ಯಾಸ, ಆರೋಗ್ಯ, IT...' : 'e.g., Teaching, Design, Healthcare, IT...'}
                      className="w-full px-4 py-3 border-2 border-neutral-200 rounded-xl text-sm focus:outline-none focus:border-saffron transition-colors" />
                  </div>
                  <div className="sm:col-span-2">
                    <label className={`block text-sm font-medium text-neutral-700 mb-1.5 ${kn ? 'font-kannada' : ''}`}>{t.getInvolved.message}</label>
                    <textarea value={form.message} onChange={(e) => setForm(f => ({ ...f, message: e.target.value }))} placeholder={kn ? 'ನೀವು ಏಕೆ ಸ್ವಯಂಸೇವಕರಾಗಲು ಬಯಸುತ್ತೀರಿ ಎಂದು ಹೇಳಿ...' : 'Tell us why you want to volunteer...'}
                      rows={3} className="w-full px-4 py-3 border-2 border-neutral-200 rounded-xl text-sm focus:outline-none focus:border-saffron transition-colors resize-none" />
                  </div>
                </div>
                <button type="submit" className={`w-full py-4 btn-saffron rounded-xl font-bold text-base text-white ${kn ? 'font-kannada' : ''}`}>
                  {t.getInvolved.submitBtn}
                </button>
              </form>
            )}
          </div>
        </section>
      </main>
      <Footer />
    </div>
  );
}
